import { along, lineDistance, lineString } from '@turf/turf';
import Color from 'color';
import ColorIO from 'colorjs.io';
import { format } from 'date-fns';
import { detect } from 'detect-browser';
import getOperatingSystemName, { isAndroid, isIOS, isMacOS, isWindows } from 'get-os-name';
import { decode, encode } from 'google-polyline';
import { JSXElement } from 'solid-js';
import { WeatherIcons } from '../components';
import { SETTINGS } from '../settings';
import { useStore } from '../store';
import colors from '../styles/colors.module.scss';
import { GenericObject, Polyline, Vec2, WeatherCurrently, WeatherLike } from '../types';
import { isHourlyWeatherArray } from '../types/guards';
import { storage } from './storage';
import { convertToUserPref, kmToMeters, metersToKm } from './units';

let locationErrorTimeoutID = 0;
const locationErrorModalTimeout = 2000;
const browser = detect();

export const safeString = (string: string) => {
  return string.toLowerCase().replace(/[^a-zA-Z0-9_]+/g, '-');
};

export const niceString = (string: string) => {
  return string.toLowerCase().replace(/[^a-zA-Z0-9]+/g, ' ');
};

export const sanitizeString = (string: string) => {
  return string.replace(/[^a-zA-Z0-9_-]+/g, '');
};

export const capitalizeFirstLetter = (string: string) => {
  return string.charAt(0).toUpperCase() + string.slice(1);
};

export const arrayUnique = (array: any[]) => {
  return [...new Set(array)];
};

export const clamp = (number: number, min: number, max: number) => {
  return Math.max(min, Math.min(number, max));
};

export const roundToNearest = (number: number, nearest: number) => {
  const decimals = String(nearest).split('.')[1]?.length || 0;
  return Number((Math.round(number / nearest) * nearest).toFixed(decimals));
};

export const objectUpdated = (newObject?: GenericObject | any[], oldObject?: GenericObject | any[]) => {
  return JSON.stringify(newObject) !== JSON.stringify(oldObject);
};

export const getCurrentPosition = (): Promise<{ lat: number; lng: number }> => {
  return new Promise((resolve, reject) => {
    if ('geolocation' in navigator) {
      navigator.geolocation.getCurrentPosition(
        position => {
          const pos = position.coords;
          resolve({
            lng: pos.longitude,
            lat: pos.latitude,
          });
        },
        e => {
          const { updateStore } = useStore();
          clearTimeout(locationErrorTimeoutID);
          // Location unavailable is normally an OS level block
          if (e.code === e.POSITION_UNAVAILABLE) {
            locationErrorTimeoutID = window.setTimeout(() => {
              updateStore.osGeolocationError(true);
            }, locationErrorModalTimeout);
            // Permission denied is normally a browser level block
          } else if (e.code === e.PERMISSION_DENIED) {
            const errorNo = storage.getItem('location_error_no');
            // https://support.google.com/chrome/answer/114662?sjid=10773281491121379968-AP
            // new flag for geolocation browser error here
            locationErrorTimeoutID = window.setTimeout(() => {
              updateStore.browserGeolocationError(true);
            }, locationErrorModalTimeout);
            // NOTE: IOS returns error PERMISSION DENIED for all errors so we need to track the number of errors
            // here and assume that maybe ist an os level block if we get 3 in a row
            if (isIosDevice()) {
              const newErrorNo = errorNo ? parseInt(errorNo) + 1 : 1;
              storage.setItem('location_error_no', String(newErrorNo));
              if (newErrorNo >= 3) {
                updateStore.browserGeolocationError(false);
                storage.removeItem('location_error_no');
                locationErrorTimeoutID = window.setTimeout(() => {
                  updateStore.osGeolocationError(true);
                }, locationErrorModalTimeout);
              }
            }
          }
          const error = `Unable to fetch current position, ${e.message || 'Geolocation blocked'}.`;
          reject(error);
        },
        { enableHighAccuracy: true, timeout: 5000, maximumAge: 0 },
      );
    } else {
      const error = 'Sorry, Geolocation is not available on this device.';
      reject(error);
    }
  });
};

export const rgbToHex = (color: [number, number, number]) => {
  // const [r, g, b] = color;
  // const componentToHex = (c: number) => {
  //   var hex = c.toString(16);
  //   return hex.length == 1 ? '0' + hex : hex;
  // };
  // const h = '#' + componentToHex(r) + componentToHex(g) + componentToHex(b);
  const converted = Color(color);
  return converted.hex();
};

export const hexToRgb = (hex: string): [number, number, number] => {
  // const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
  // return result ? [parseInt(result[1], 16), parseInt(result[2], 16), parseInt(result[3], 16)] : [0, 0, 0];
  const converted = new Color(hex).rgb();
  // TODO: fix types
  return (converted as any).color;
};

export const randomBetween = (min: number, max: number, decimalPlaces = 0) => {
  if (decimalPlaces === 0) {
    return Math.floor(Math.random() * (max - min + 1) + min);
  } else {
    return parseFloat(Math.min(min + Math.random() * (max - min), max).toFixed(decimalPlaces));
  }
};

export const roundToXDecimalPlaces = (number: number, decimalPlaces = 1) => {
  return parseFloat(number.toFixed(decimalPlaces));
};

export const getDistanceBetween = (point1: Vec2, point2: Vec2) => {
  return Math.hypot(point2[0] - point1[0], point2[1] - point1[1]);
};

export const decodePolylineForMapbox = (encodedLine: string): Polyline => {
  const polyline = decode(encodedLine);
  return flipPolylineForMapbox(polyline);
};

export const flipPolylineForMapbox = (polyline: Polyline): Polyline => {
  return polyline.map(point => {
    return [point[1], point[0]];
  });
};

export const reversePolyline = (line: string) => {
  const polyline = decode(line);
  const reversed = polyline.reverse();
  return encode(reversed);
};

export const resamplePolyline = (polyline: Polyline, intervalKms: number) => {
  const line = lineString(flipPolylineForMapbox(polyline));
  const dist = lineDistance(line, { units: 'kilometers' });
  const newPoints: Polyline = [];
  for (var i = 0; i < dist; i += intervalKms) {
    const point = along(line, i, { units: 'kilometers' });
    const coords = point.geometry.coordinates;
    newPoints.push([coords[1], coords[0]]);
  }
  return newPoints;
};

export const secondsToNiceTime = (seconds: number, includeSeconds = false) => {
  const format = (val: number) => `0${Math.floor(val)}`.slice(-2);
  const hours = seconds / 3600;
  const minutes = (seconds % 3600) / 60;
  if (includeSeconds) {
    return [hours, minutes, seconds % 60].map(format).join(':');
  } else {
    return [hours, minutes].map(format).join(':');
  }
};

// TODO: type any
export const isDescendant = (parent: any, child: any) => {
  if (!child || !parent || !child.parentNode) {
    return false;
  }
  let node = child.parentNode;
  while (node) {
    if (node === parent) {
      return true;
    }
    node = node.parentNode;
  }
  return false;
};

export const isInstalled = () => {
  return (
    window.matchMedia('(display-mode: standalone)').matches ||
    (window.navigator as any).standalone ||
    window.location.search.includes('installed=1')
  );
};

export const solidComponentToHTMLElement = (JSX: JSXElement) => {
  if (typeof JSX === 'function') {
    const renderer = JSX as any as () => HTMLElement;
    return JSX ? renderer() : undefined;
  }
  return JSX as HTMLElement;
};

export const getWindHardnessColor = (windSpeed: number) => {
  const easy = Color(colors.tertiary);
  const hard = Color(colors.primary);
  const range = SETTINGS.HARDNESS_WIND_GETS_HARD_AT - SETTINGS.HARDNESS_WIND_GETS_EASY_AT;
  const speed =
    Math.min(Math.max(SETTINGS.HARDNESS_WIND_GETS_EASY_AT, windSpeed), SETTINGS.HARDNESS_WIND_GETS_HARD_AT) -
    SETTINGS.HARDNESS_WIND_GETS_EASY_AT;
  const hardness = (Math.min(100, (speed / range) * 100) / 100).toPrecision(2);
  const color = easy.mix(hard, parseFloat(hardness));
  return color.hex();
};

export const getNiceDistance = (distance: number) => {
  return convertToUserPref(metersToKm(distance), 'distance');
};

export const calculateCalories = (sex: 'M' | 'F', speed: number, windSpeed: number, hours: number) => {
  const riderWeight =
    sex === 'M' ? SETTINGS.CALORIE_CALCULATION_AVERAGE_MALE_WEIGHT : SETTINGS.CALORIE_CALCULATION_AVERAGE_FEMALE_WEIGHT;
  const weight =
    riderWeight + SETTINGS.CALORIE_CALCULATION_AVERAGE_BIKE_WEIGHT - SETTINGS.CALORIE_CALCULATION_REALITY_ADJUSTMENT;
  const calories =
    ((9.8 * weight * speed * (0.0053 + 1) + 0.185 * Math.pow(windSpeed, 2) * speed) * 1.16 * hours) / 100;
  return Math.round(calories);
};

export const calculateWindCalories = (calories: number, hardness = 2.5) => {
  if (isNaN(hardness)) {
    hardness = 2.5;
  }
  let percentage = (hardness * 100) / 5 / 400;
  const modifier = percentage * calories;
  return Math.round(calories + modifier);
};

export const getPolylineDistance = (polyline: Polyline) => {
  if (polyline.length > 1) {
    const flipped = flipPolylineForMapbox(polyline);
    const totalDistance = lineDistance(lineString(flipped));
    return kmToMeters(totalDistance);
  }
  return 0;
};

export const isSharedOrEmbeddedRide = () => {
  return window.location.pathname.includes('/share/') || window.location.pathname.includes('/embed/');
};

export const isEmbeddedRide = () => {
  return window.location.pathname.includes('/embed/');
};

export const isSharedRide = () => {
  return window.location.pathname.includes('/share/');
};

export const isDemo = () => {
  const { store } = useStore();
  return store().stravaToken?.access_token === SETTINGS.DEMO_STRAVA_TOKEN;
};

export const isSubscribed = () => {
  const { store } = useStore();
  return store().settings.donated ? true : false;
};

export const isSafari = () => {
  if (isIosDevice()) {
    return browser?.name === 'ios';
  }
  // TODO: test on mac
  return browser?.name === 'safari';
};

export const isChrome = () => {
  if (isIosDevice()) {
    return browser?.name === 'crios';
  }
  return browser?.name === 'chrome';
};

export const getBrowser = () => {
  return browser?.name || 'chrome';
};

export const isIosDevice = () => {
  return isIOS();
};

export const isMacDevice = () => {
  return isMacOS();
};

export const isWindowsDevice = () => {
  return isWindows();
};

export const isAndroidDevice = () => {
  return isAndroid();
};

export const isMobileDevice = () => {
  return isAndroidDevice() || isIosDevice();
};

export const getOS = () => {
  return getOperatingSystemName();
};

export const isAdmin = () => {
  const { store } = useStore();
  if (SETTINGS.ADMIN_STRAVA_ID === store().stravaUserID) {
    return true;
  }
  return false;
};

export const isDevMode = () => {
  return import.meta.env.DEV;
};

export const isLiveEnvironment = () => {
  return window.location.hostname === 'headwind.app';
};

export const isDevelopEnvironment = () => {
  return window.location.hostname === 'develop.headwind.app';
};

export const isLoggedIn = () => {
  const { store } = useStore();
  return store().stravaUserID && store().stravaToken ? true : false;
};

export const normalizeDate = (date?: Date) => {
  return format(date || new Date(), "yyyy-MM-dd'T'HH:mm:ss");
};

export const easeHardnessInOutQuad = (t: number, b: number, c: number, d: number) => {
  if ((t /= d / 2) < 1) return (c / 2) * t * t + b;
  return (-c / 2) * (--t * (t - 2) - 1) + b;
};

export const getLineAngle = (p1: Vec2, p2: Vec2) => {
  let angle = (Math.atan2(p2[1] - p1[1], p2[0] - p1[0]) * 180) / Math.PI;
  if (angle < 0) {
    angle = 360 + angle;
  }
  return angle;
};

export const latLngToPoint = (lat: number, lng: number, multiplier = 1): Vec2 => {
  return [
    (lng + 180) * (256 / 360) * multiplier,
    256 / 2 - ((256 * Math.log(Math.tan(Math.PI / 4 + (lat * Math.PI) / 180 / 2))) / (2 * Math.PI)) * multiplier,
  ];
};

export const getWeatherIcon = (weather: WeatherLike) => {
  const current = weather.currently || weather;
  const hour = Number(format(new Date(current.time * 1000), 'HH'));
  let severeRisk = weather.severerisk || 0;
  let precipTypes = weather.precipType || [];
  if (isHourlyWeatherArray(weather?.hourly?.data)) {
    const currentHour = weather.hourly.data[hour];
    severeRisk = currentHour.severerisk || 0;
    precipTypes = currentHour.precipType || [];
  }
  const icon = [];
  if (severeRisk < 70) {
    let precipType = '';
    let cloud = 'clear';
    let precip = 'clear';
    // rain or snow
    if (precipTypes.includes('snow') || precipTypes.includes('freezingrain')) {
      precipType = 'snow';
    } else if (precipTypes.includes('rain')) {
      precipType = 'rain';
    }
    // how much rain or snow
    const daily = current.precipIntensity * 24;
    if (precipType && daily > 1) {
      if (daily > 9) {
        precip = `${precipType}-4`;
      } else if (daily > 6) {
        precip = `${precipType}-3`;
      } else if (daily > 3) {
        precip = `${precipType}-2`;
      } else {
        precip = `${precipType}-1`;
      }
      icon.push(precip);
      // else no rain or snow so just use cloud
    } else {
      if (current.cloudCover > 0.8) {
        cloud = 'cloud-4';
      } else if (current.cloudCover > 0.6) {
        cloud = 'cloud-3';
      } else if (current.cloudCover > 0.4) {
        cloud = 'cloud-2';
      } else if (current.cloudCover > 0.2) {
        cloud = 'cloud-1';
      }
      icon.push(cloud);
    }
    let cycle = 'day';
    if (current.sunriseEpoch && current.sunsetEpoch) {
      const time = current.time * 1000;
      if (time < current.sunriseEpoch || time > current.sunsetEpoch) {
        cycle = 'night';
      }
    } else {
      if (hour < 6 || hour > 19) {
        cycle = 'night';
      }
    }
    icon.push(cycle);
  } else {
    icon.push('storm');
  }
  // cloud fallback
  if (!icon.length) {
    icon.push('cloud-1-day');
  }
  const iconPath = icon.join('-');
  return iconPath as WeatherIcons;
};

// precipIntensity is mm/hr. ~7.5mm/hr is meteorologically "heavy rain", so that's
// treated as full effect strength; a light drizzle like 0.9mm/hr stays subtle.
export const getRainIntensity = (current: WeatherCurrently) => {
  const precipTypes = current.precipType || current.preciptype || [];
  if (!precipTypes.includes('rain')) return 0;
  return clamp(current.precipIntensity / 7.5, 0, 1);
};

export const oneDecPlace = (n: number) => {
  return Math.ceil(n * 10) / 10;
};

export const twoDecPlace = (n: number) => {
  return Math.round((n + Number.EPSILON) * 100) / 100;
};

export const formatApiTimestamp = (stamp: number, pattern: string) => {
  // return format(stamp * 1000, pattern);
  return format(stamp, pattern);
};

export const interpolateColors = (color1: string, color2: string, factor: number) => {
  const c1 = new ColorIO(color1);
  const c2 = new ColorIO(color2);
  const interpolate = c1.range(c2, {
    space: 'xyz', // interpolation space
    outputSpace: 'srgb',
  });
  const color = interpolate(factor);
  return color.to('srgb').toString({ format: 'hex' });
};

export const imageUrlToBase64 = (url: string): Promise<string> => {
  return new Promise(async (resolve, reject) => {
    try {
      const response = await fetch(url);
      const blob = await response.blob();
      const reader = new FileReader();
      reader.onloadend = () => resolve(String(reader.result));
      reader.onerror = reject;
      reader.readAsDataURL(blob);
    } catch (e) {
      reject(e);
    }
  });
};
